import React from "react";
import { Typography } from "antd";

const { Title, Text, Paragraph, Link } = Typography;

/**
 * CustomTypography - A fully reusable Ant Design Typography component
 *
 * @example
 * // Title examples
 * <CustomTypography variant="title" level={1}>Main Heading</CustomTypography>
 * <CustomTypography variant="title" level={2} type="secondary">Subheading</CustomTypography>
 *
 * // Text examples
 * <CustomTypography variant="text">Normal text</CustomTypography>
 * <CustomTypography variant="text" type="success" strong>Success message</CustomTypography>
 *
 * // Paragraph examples
 * <CustomTypography variant="paragraph" ellipsis>Long paragraph text...</CustomTypography>
 *
 * // Link examples
 * <CustomTypography variant="link" href="https://example.com">External Link</CustomTypography>
 *
 * // Advanced features
 * <CustomTypography
 *   variant="text"
 *   copyable={{ text: "Copy this text" }}
 *   editable={{ onStart: () => console.log('edit start') }}
 * >
 *   Editable and copyable text
 * </CustomTypography>
 */

const CustomTypography = ({
  variant = "text",
  children,
  level = 1,
  type,
  disabled = false,
  mark = false,
  code = false,
  keyboard = false,
  underline = false,
  delete: deleteProp = false,
  strong = false,
  italic = false,
  copyable = false,
  editable = false,
  ellipsis = false,
  href,
  target,
  className = "",
  style = {},
  onClick,
  // Title specific props
  titleProps = {},
  // Text specific props
  textProps = {},
  // Paragraph specific props
  paragraphProps = {},
  // Link specific props
  linkProps = {},
  ...rest
}) => {
  // Common props that apply to all typography variants
  const commonProps = {
    type,
    disabled,
    className,
    style,
    onClick,
    ...rest,
  };

  // Text formatting props
  const formatProps = {
    mark,
    code,
    keyboard,
    underline,
    delete: deleteProp,
    strong,
    italic,
  };

  // Interactive props
  const interactiveProps = {
    copyable,
    editable,
  };

  switch (variant) {
    case "title":
      return (
        <Title
          level={level}
          ellipsis={ellipsis}
          {...commonProps}
          {...formatProps}
          {...interactiveProps}
          {...titleProps}
        >
          {children}
        </Title>
      );

    case "text":
      return (
        <Text
          ellipsis={ellipsis}
          {...commonProps}
          {...formatProps}
          {...interactiveProps}
          {...textProps}
        >
          {children}
        </Text>
      );

    case "paragraph":
      return (
        <Paragraph
          ellipsis={ellipsis}
          {...commonProps}
          {...formatProps}
          {...interactiveProps}
          {...paragraphProps}
        >
          {children}
        </Paragraph>
      );

    case "link":
      return (
        <Link
          href={href}
          target={target}
          ellipsis={ellipsis}
          {...commonProps}
          {...formatProps}
          {...linkProps}
        >
          {children}
        </Link>
      );

    default:
      return (
        <Text
          ellipsis={ellipsis}
          {...commonProps}
          {...formatProps}
          {...interactiveProps}
          {...textProps}
        >
          {children}
        </Text>
      );
  }
};

// ============================================================================
// PREDEFINED VARIANTS - Quick Reference Guide
// ============================================================================
//
// 📌 HEADINGS (for titles and section headers)
// ----------------------------------------
// CustomTypography.Heading1 - Largest heading (H1) - Page titles
// CustomTypography.Heading2 - Second level heading (H2) - Section headers
// CustomTypography.Heading3 - Third level heading (H3) - Subsection headers
// CustomTypography.Heading4 - Fourth level heading (H4) - Small headers
// CustomTypography.Heading5 - Fifth level heading (H5) - Smallest headers
//
// 📝 TEXT SIZES (for body content)
// ----------------------------------------
// CustomTypography.BodyLarge   - Large text (16px) - Emphasis content
// CustomTypography.Body        - Normal text (14px) - ⭐ TEXT BASE/NORMAL ⭐ - Default body text
// CustomTypography.BodySmall   - Small text (12px) - Fine print, footnotes
// CustomTypography.Caption     - Caption text (12px, secondary) - Image captions, helper text
//
// 🎨 TEXT COLORS (for semantic meaning)
// ----------------------------------------
// CustomTypography.Success     - Green text - Success messages
// CustomTypography.Warning     - Orange text - Warning messages
// CustomTypography.Danger      - Red text - Error messages
// CustomTypography.Secondary   - Gray text - Less important text
//
// 🔗 SPECIAL TYPES
// ----------------------------------------
// CustomTypography.Link        - Hyperlinks
// CustomTypography.Code        - Code snippets (monospace)
// CustomTypography.Strong      - Bold text
// CustomTypography.Italic      - Italic text
// CustomTypography.Mark        - Highlighted text
//
// 💡 USAGE EXAMPLES:
// ----------------------------------------
// Text Base/Normal:     <CustomTypography.Body>Normal text here</CustomTypography.Body>
// Bold text:            <CustomTypography.Body strong>Bold text</CustomTypography.Body>
// Combined:             <CustomTypography.BodyLarge type="success" strong>Large bold success</CustomTypography.BodyLarge>
// ============================================================================

// HEADINGS
const Heading1 = props => <CustomTypography variant="title" level={1} {...props} />;
Heading1.displayName = "CustomTypography.Heading1";
CustomTypography.Heading1 = Heading1;

const Heading2 = props => <CustomTypography variant="title" level={2} {...props} />;
Heading2.displayName = "CustomTypography.Heading2";
CustomTypography.Heading2 = Heading2;

const Heading3 = props => <CustomTypography variant="title" level={3} {...props} />;
Heading3.displayName = "CustomTypography.Heading3";
CustomTypography.Heading3 = Heading3;

const Heading4 = props => <CustomTypography variant="title" level={4} {...props} />;
Heading4.displayName = "CustomTypography.Heading4";
CustomTypography.Heading4 = Heading4;

const Heading5 = props => <CustomTypography variant="title" level={5} {...props} />;
Heading5.displayName = "CustomTypography.Heading5";
CustomTypography.Heading5 = Heading5;

// TEXT SIZES
const Body = props => <CustomTypography variant="text" {...props} />; // ⭐ TEXT BASE/NORMAL
Body.displayName = "CustomTypography.Body";
CustomTypography.Body = Body;

const BodyLarge = props => (
  <CustomTypography variant="text" style={{ fontSize: "16px" }} {...props} />
);
BodyLarge.displayName = "CustomTypography.BodyLarge";
CustomTypography.BodyLarge = BodyLarge;

const BodySmall = props => (
  <CustomTypography variant="text" style={{ fontSize: "12px" }} {...props} />
);
BodySmall.displayName = "CustomTypography.BodySmall";
CustomTypography.BodySmall = BodySmall;

const Caption = props => (
  <CustomTypography variant="text" type="secondary" style={{ fontSize: "12px" }} {...props} />
);
Caption.displayName = "CustomTypography.Caption";
CustomTypography.Caption = Caption;

// TEXT COLORS
const Success = props => <CustomTypography variant="text" type="success" {...props} />;
Success.displayName = "CustomTypography.Success";
CustomTypography.Success = Success;

const Warning = props => <CustomTypography variant="text" type="warning" {...props} />;
Warning.displayName = "CustomTypography.Warning";
CustomTypography.Warning = Warning;

const Danger = props => <CustomTypography variant="text" type="danger" {...props} />;
Danger.displayName = "CustomTypography.Danger";
CustomTypography.Danger = Danger;

const Secondary = props => <CustomTypography variant="text" type="secondary" {...props} />;
Secondary.displayName = "CustomTypography.Secondary";
CustomTypography.Secondary = Secondary;

const LinkComponent = props => <CustomTypography variant="link" {...props} />;
LinkComponent.displayName = "CustomTypography.Link";
CustomTypography.Link = LinkComponent;

// SPECIAL TYPES
const CustomLink = props => <CustomTypography variant="link" {...props} />;
CustomLink.displayName = "CustomTypography.Link";
CustomTypography.Link = CustomLink;

const Code = props => <CustomTypography variant="text" code {...props} />;
Code.displayName = "CustomTypography.Code";
CustomTypography.Code = Code;

const Strong = props => <CustomTypography variant="text" strong {...props} />;
Strong.displayName = "CustomTypography.Strong";
CustomTypography.Strong = Strong;

const Italic = props => <CustomTypography variant="text" italic {...props} />;
Italic.displayName = "CustomTypography.Italic";
CustomTypography.Italic = Italic;

const Mark = props => <CustomTypography variant="text" mark {...props} />;
Mark.displayName = "CustomTypography.Mark";
CustomTypography.Mark = Mark;

export default CustomTypography;
